import { DestroyRef, inject, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  NgModel,
  UntypedFormControl,
} from '@angular/forms';

export abstract class ReactiveComponent implements ControlValueAccessor {
  disabled: boolean = false;
  touched: boolean = false;
  invalid = signal(false);

  protected injector = inject(Injector);
  protected destroyRef = inject(DestroyRef);
  protected control: UntypedFormControl;

  protected initializeControl(): UntypedFormControl {
    if (this.control != null) {
      return this.control;
    }

    const control = this.injector.get(NgControl);
    let formControl: UntypedFormControl;

    if (control instanceof NgModel) {
      formControl = control.control;
    } else if (control instanceof FormControlName) {
      const formGroup = this.injector.get(FormGroupDirective);

      formControl = formGroup.getControl(control);
    } else {
      formControl = (control as FormControlDirective).control;
    }

    this.control = formControl;
    this.control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
      this.invalid.set(status === 'INVALID');
    });
    this.invalid.set(formControl.status === 'INVALID');

    return this.control;
  }

  // ControlValueAccessor functions
  onChange(value) {}
  onTouched() {}
  onValidatorChanged() {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChanged = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  abstract writeValue(obj: any): void;
}
